import { useMemo } from 'react'
import axios from 'axios'
import { useLocation } from 'react-router-dom'
import { MaintenanceModal } from '../../../../components/templates/MaintenanceModal'
import { useHealthCheckQuery } from '../../../health'
import { isPublicPath } from '../../../../constants/publicPaths'
import { isCertTlsRejection } from '../../../../helpers/isCertTlsRejection'

/**
 * メンテナンスモーダル表示 hook（TanStack Query版）
 *
 * 10秒間隔でヘルスチェックを実行し、
 * エラー時（401/302を除く）はメンテナンスモーダルを表示する。
 *
 * PUBLIC_PATHS（未認証で閲覧するページ）ではヘルスチェック自体を行わない。
 * ガイドはバックエンド停止中も読める必要があるため。
 */
export const useShowMaintenanceModal = (): JSX.Element => {
  const { pathname } = useLocation()
  const isPublic = isPublicPath(pathname)

  const { error } = useHealthCheckQuery({ enabled: !isPublic })

  const isMaintenanceError = useMemo(() => {
    // enabled:false でも他画面で発生した error はキャッシュから返り続けるため、
    // 公開ページでは表示判定でも抑止する
    if (isPublic) return false
    if (!error) return false
    // cert環境のTLS層拒否（無応答＝証明書未選択/失効/非信頼）はメンテではないため
    // モーダルを出さず、ログイン画面のcert再選択ガイドに委ねる
    // （応答あり=5xx等は従来どおりメンテ扱い。判定はログイン画面と同じ関数）
    if (isCertTlsRejection(error)) {
      return false
    }
    // 401エラー（認証エラー）と302リダイレクトは正常として扱う（ログインページで処理される）
    const status = axios.isAxiosError(error)
      ? error.response?.status
      : undefined
    return status !== 401 && status !== 302
  }, [error, isPublic])

  return <MaintenanceModal isOpen={isMaintenanceError} />
}
